/**
 * Pure pieces of the top-bar trial chip (MOT-702): the shape
 * `GET /billing/trial-status` returns and how the chip reads it.
 */

/** A feature the trial gives that the store loses when it ends (MOT-729). */
export interface TrialFeature {
  /** A boolean `PlanLimits` key, e.g. `rebateTracking`. */
  key: string;
  /** The cheapest tier that sells it. */
  tier: string;
  /** That tier's name, e.g. "Pro". */
  tierName: string;
}

export interface TrialStatus {
  source: 'access_grant' | 'stripe';
  planName: string;
  tier: string | null;
  endsAt: string | null;
  daysLeft: number | null;
  afterTrialPlanName: string | null;
  /** Missing from a response written before MOT-729; read through `trialFeatureFor`. */
  trialFeatures?: TrialFeature[];
}

export interface TrialStatusResponse {
  trial: TrialStatus | null;
  canManageBilling: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Days left at `nowMs`, a part day counting as one, the server's rule; 0 once
 * the trial has ended, null with no end date. Worked out in the browser so the
 * count, the tone and the chip itself keep up while the page stays open.
 */
export const liveDaysLeft = (endsAt: string | null, nowMs: number): number | null => {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - nowMs) / DAY_MS));
};

/** The last three days turn the chip amber. */
export const TRIAL_CAUTION_DAYS = 3;

export type TrialChipTone = 'info' | 'caution';

export const trialChipTone = (daysLeft: number | null): TrialChipTone =>
  daysLeft !== null && daysLeft <= TRIAL_CAUTION_DAYS ? 'caution' : 'info';

/** Which body line the popover shows, and the values it needs. */
export type TrialBodyKey = 'endsStripe' | 'endsGrant' | 'endsGrantNoAfter' | 'noEnd';

export const trialBodyKey = (trial: TrialStatus): TrialBodyKey => {
  if (!trial.endsAt) return 'noEnd';
  if (trial.source === 'stripe') return 'endsStripe';
  return trial.afterTrialPlanName ? 'endsGrant' : 'endsGrantNoAfter';
};

export const formatTrialEnd = (endsAt: string, language: string): string =>
  new Date(endsAt).toLocaleDateString(language || 'en', { month: 'long', day: 'numeric', year: 'numeric' });

/**
 * The tier badge a feature carries while the trial runs, or null when the
 * store keeps the feature after it (or there is no live trial). A trial that
 * ended while the page stayed open carries no badges.
 */
export const trialFeatureFor = (
  trial: TrialStatus | null | undefined,
  feature: string,
  nowMs: number,
): TrialFeature | null => {
  if (!trial || liveDaysLeft(trial.endsAt, nowMs) === 0) return null;
  return trial.trialFeatures?.find((entry) => entry.key === feature) ?? null;
};
